import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { Platform } from 'react-native';
import { ImageUploadResult, ImageUploadProgress } from '../types';

/**
 * Image service for handling uploads, compression, and URL generation
 */
class ImageService {
  private cloudName: string;
  private uploadPreset: string;

  constructor() {
    this.cloudName = process.env.EXPO_PUBLIC_CLOUDINARY_CLOUD_NAME || '';
    this.uploadPreset = process.env.EXPO_PUBLIC_CLOUDINARY_UPLOAD_PRESET || '';

    if (!this.cloudName || !this.uploadPreset) {
      console.warn('Cloudinary credentials not configured');
    }
  }

  /**
   * Pick images from device library
   * @param maxImages Maximum number of images to select
   * @returns ImagePicker result with selected images
   */
  async pickImages(maxImages: number = 2): Promise<ImagePicker.ImagePickerResult> {
    // Request media library permissions
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();

    if (status !== 'granted') {
      throw new Error('Permission to access media library was denied');
    }

    // Launch image picker
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsMultipleSelection: true,
      quality: 1, // We'll compress it ourselves
      selectionLimit: maxImages,
    });

    return result;
  }

  /**
   * Resize image and preserve transparency
   * Uses PNG format to preserve transparency (e.g., for club logos)
   * Cloudinary handles final optimization via f_auto and q_auto parameters
   * @param uri Local URI of the image to process
   * @returns URI of the resized image
   */
  async compressImage(uri: string): Promise<string> {
    try {
      const manipResult = await ImageManipulator.manipulateAsync(
        uri,
        [{ resize: { width: 1200 } }], // Maintain aspect ratio, max width 1200px
        {
          compress: 1, // Preserve quality - let Cloudinary optimize
          format: ImageManipulator.SaveFormat.PNG, // Preserve transparency for logos
        }
      );

      return manipResult.uri;
    } catch (error) {
      console.error('Image resize failed, uploading original:', error);
      // Fallback: return original URI if resize fails
      return uri;
    }
  }

  /**
   * Upload image to Cloudinary
   * @param imageUri Local URI of the compressed image
   * @param folder Cloudinary folder path (default: 'clubswap/listings')
   * @returns Cloudinary public ID
   */
  async uploadToCloudinary(imageUri: string, folder: string = 'clubswap/listings'): Promise<string> {
    try {
      // Create form data
      const formData = new FormData();

      // Extract filename from URI
      const filename = imageUri.split('/').pop() || 'upload.jpg';

      // Handle web vs mobile differently
      if (Platform.OS === 'web') {
        // Web: Convert data URI to Blob
        const response = await fetch(imageUri);
        const blob = await response.blob();
        formData.append('file', blob, filename);
      } else {
        // Mobile: Use URI object format
        formData.append('file', {
          uri: imageUri,
          type: 'image/jpeg',
          name: filename,
        } as any);
      }

      // Add upload preset
      formData.append('upload_preset', this.uploadPreset);

      // Add folder
      formData.append('folder', folder);

      // Upload to Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cloudinary upload error:', errorData);
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();
      console.log('Cloudinary upload response:', {
        public_id: data.public_id,
        secure_url: data.secure_url,
        format: data.format,
        width: data.width,
        height: data.height
      });
      return data.public_id;
    } catch (error) {
      console.error('Cloudinary upload failed:', error);
      throw new Error('Failed to upload image');
    }
  }

  /**
   * Upload multiple images with progress tracking
   * @param uris Array of local image URIs
   * @param onProgress Callback for upload progress
   * @returns Array of Cloudinary public IDs
   */
  async uploadMultipleImages(
    uris: string[],
    onProgress?: (progress: ImageUploadProgress) => void
  ): Promise<string[]> {
    const publicIds: string[] = [];
    const total = uris.length;

    for (let i = 0; i < uris.length; i++) {
      const uri = uris[i];

      // Compress image
      const compressedUri = await this.compressImage(uri);

      // Upload to Cloudinary
      const publicId = await this.uploadToCloudinary(compressedUri);
      publicIds.push(publicId);

      // Report progress
      if (onProgress) {
        onProgress({
          loaded: i + 1,
          total,
          percentage: ((i + 1) / total) * 100,
        });
      }
    }

    return publicIds;
  }

  /**
   * Generate thumbnail URL (300x300)
   * @param publicId Cloudinary public ID
   * @returns Optimized thumbnail URL
   */
  getThumbnailUrl(publicId: string): string {
    if (!this.cloudName) {
      console.error('Cloudinary cloud name not configured!');
    }
    const url = `https://res.cloudinary.com/${this.cloudName}/image/upload/w_300,h_300,c_fill,q_auto,f_auto/${publicId}`;
    return url;
  }

  /**
   * Generate small square thumbnail URL (100x100) for list items
   * @param publicId Cloudinary public ID
   * @returns Optimized small thumbnail URL
   */
  getSmallThumbnailUrl(publicId: string): string {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_100,h_100,c_fill,q_auto,f_auto/${publicId}`;
  }

  /**
   * Generate circular thumbnail URL (100x100) for dropdowns
   * Uses smart face detection crop and maximum rounding
   * @param publicId Cloudinary public ID
   * @returns Optimized circular thumbnail URL
   */
  getCircularThumbnailUrl(publicId: string): string {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_100,h_100,c_fill,g_face,r_max,q_auto,f_auto/${publicId}`;
  }

  /**
   * Generate logo URL that preserves entire logo without cropping
   * Uses fit mode with transparent padding for both landscape and portrait logos
   * @param publicId Cloudinary public ID
   * @param size Logo size (small: 120px, medium: 200px, large: 300px)
   * @returns Optimized logo URL with transparent background
   */
  getLogoUrl(publicId: string, size: 'small' | 'medium' | 'large' = 'medium'): string {
    const dimensions = {
      small: 120,
      medium: 200,
      large: 300
    };
    const dim = dimensions[size];
    // c_pad = fit entire image with padding, b_rgb:ffffff = white background for transparency
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_${dim},h_${dim},c_pad,b_rgb:ffffff,q_auto,f_auto/${publicId}`;
  }

  /**
   * Generate full-size URL (1200px max width)
   * @param publicId Cloudinary public ID
   * @returns Optimized full-size URL
   */
  getFullSizeUrl(publicId: string): string {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/w_1200,q_auto:good,f_auto/${publicId}`;
  }

  /**
   * Upload single image (for club logos)
   * @param uri Local image URI
   * @param folder Cloudinary folder path (default: 'clubswap/listings')
   * @param onProgress Optional progress callback
   * @returns Cloudinary public ID
   */
  async uploadSingleImage(
    uri: string,
    folder: string = 'clubswap/listings',
    onProgress?: (progress: number) => void
  ): Promise<string> {
    if (onProgress) onProgress(0);

    // Compress image
    const compressedUri = await this.compressImage(uri);
    if (onProgress) onProgress(50);

    // Upload to Cloudinary
    const publicId = await this.uploadToCloudinary(compressedUri, folder);
    if (onProgress) onProgress(100);

    return publicId;
  }

  /**
   * Validate image count
   * @param current Current number of images
   * @param max Maximum allowed images
   * @returns True if valid, false otherwise
   */
  validateImageCount(current: number, max: number): boolean {
    return current < max;
  }
}

// Export singleton instance
export const imageService = new ImageService();
